import {
  EnvironmentLogType,
  type EnvironmentLogSummary,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import type { Timestamp } from "@bufbuild/protobuf";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { LogViewer } from "components/LogViewer";
import { Button } from "components/Graphs/Button";
import { Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import {
  fetchHistoricalEnvironmentLogs,
  listEnvironmentLogHistory,
} from "server/devplane/environmentLogs";

interface PastVMLogsProps {
  excludedEnvironmentLogTypes?: ReadonlySet<string>;
  refreshAfterBuildCompletion: boolean;
  environmentId: string;
}

const BUILD_COMPLETION_HISTORY_REFRESH_DELAY_MS = 5_000;
const NO_EXCLUDED_ENVIRONMENT_LOG_TYPES = new Set<string>();
const OTHER_LOG_EXCLUDED_TYPES = new Set(["lifecycle-script", "vm-build"]);

const formatTimestamp = (timestamp?: Timestamp): string => {
  if (!timestamp) return "Unknown time";
  return timestamp.toDate().toLocaleString();
};

const formatLogSize = (sizeBytes: bigint): string => {
  const bytes = Number(sizeBytes);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** unitIndex;
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const getResultLabel = (resultStatus: string): string => {
  if (resultStatus === "success") return "Succeeded";
  if (resultStatus === "fail") return "Failed";
  return "Completed";
};

const getEnvironmentLogTypeDetails = (
  environmentLogType: EnvironmentLogType,
): { key: string; label: string } => {
  switch (environmentLogType) {
    case EnvironmentLogType.VERB_BUILD:
      return { key: "verb-build", label: "Build" };
    case EnvironmentLogType.VM_BUILD:
      return { key: "vm-build", label: "VM setup" };
    case EnvironmentLogType.CONTAINER_BUILD:
      return { key: "container-build", label: "Container setup" };
    case EnvironmentLogType.DOCKER_COMPOSE_BUILD:
      return { key: "docker-compose-build", label: "Docker Compose" };
    case EnvironmentLogType.LIFECYCLE_SCRIPT:
      return { key: "lifecycle-script", label: "Lifecycle script" };
    case EnvironmentLogType.GENERIC:
    case EnvironmentLogType.UNSPECIFIED:
      return { key: "generic", label: "General" };
    default:
      return { key: "unknown", label: "Unknown" };
  }
};

const HistoricalLogViewer: React.FC<{
  environmentId: string;
  item: EnvironmentLogSummary;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}> = ({ environmentId, item, isOpen, onOpenChange }) => {
  const logsQuery = useQuery({
    queryKey: [
      "environment-log-history-content",
      environmentId,
      item.environmentLogId,
    ],
    queryFn: () =>
      fetchHistoricalEnvironmentLogs(environmentId, item.environmentLogId),
    enabled: isOpen,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const type = getEnvironmentLogTypeDetails(item.environmentLogType);

  return (
    <LogViewer
      logs={
        logsQuery.isLoading
          ? "Loading logs..."
          : logsQuery.isError
            ? "These logs could not be loaded. The stored artifact may no longer be available."
            : (logsQuery.data?.content ?? "")
      }
      didSucceed={item.resultStatus === "success"}
      didFail={item.resultStatus === "fail"}
      title={formatTimestamp(item.createTime)}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      headerDetails={[
        type.label === "General" ? undefined : type.label,
        getResultLabel(item.resultStatus),
        item.exitCode !== undefined ? `Exit ${item.exitCode}` : undefined,
        formatLogSize(item.logSizeBytes),
      ]
        .filter(Boolean)
        .join(" · ")}
      headerDetailsInline
    />
  );
};

export const PastVMLogs: React.FC<PastVMLogsProps> = ({
  excludedEnvironmentLogTypes = NO_EXCLUDED_ENVIRONMENT_LOG_TYPES,
  environmentId,
  refreshAfterBuildCompletion,
}) => {
  const [openEnvironmentLogId, setOpenEnvironmentLogId] = useState("");
  const [historyRefreshVersion, setHistoryRefreshVersion] = useState(0);

  useEffect(() => {
    if (!refreshAfterBuildCompletion || !environmentId) return;

    const timeout = window.setTimeout(() => {
      setHistoryRefreshVersion((version) => version + 1);
    }, BUILD_COMPLETION_HISTORY_REFRESH_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [environmentId, refreshAfterBuildCompletion]);

  const historyQuery = useInfiniteQuery({
    queryKey: ["environment-log-history", environmentId, historyRefreshVersion],
    queryFn: ({ pageParam = "" }) =>
      listEnvironmentLogHistory(environmentId, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    enabled: Boolean(environmentId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const historyItems = useMemo(() => {
    const items = historyQuery.data?.pages.flatMap((page) => page.items) ?? [];

    return items.filter((item) => {
      const type = getEnvironmentLogTypeDetails(item.environmentLogType).key;
      return (
        !OTHER_LOG_EXCLUDED_TYPES.has(type) &&
        !excludedEnvironmentLogTypes.has(type)
      );
    });
  }, [excludedEnvironmentLogTypes, historyQuery.data]);
  return (
    <div>
      {historyQuery.isLoading && (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading other logs...
        </div>
      )}

      {historyQuery.isError && (
        <p className="py-4 text-sm text-red-500">
          Other logs could not be loaded. Please try again.
        </p>
      )}

      {!historyQuery.isLoading &&
        !historyQuery.isError &&
        historyItems.length === 0 && (
          <p className="py-4 text-sm text-gray-600 dark:text-zinc-400">
            No other logs are available for this environment.
          </p>
        )}

      {historyItems.length > 0 && (
        <div className="space-y-2">
          {historyItems.map((item) =>
            item.logsAvailable ? (
              <HistoricalLogViewer
                key={item.environmentLogId}
                environmentId={environmentId}
                item={item}
                isOpen={openEnvironmentLogId === item.environmentLogId}
                onOpenChange={(isOpen) =>
                  setOpenEnvironmentLogId(isOpen ? item.environmentLogId : "")
                }
              />
            ) : (
              <p
                key={item.environmentLogId}
                className="rounded-md border border-zinc-800 px-4 py-3 text-sm text-secondary"
              >
                {formatTimestamp(item.createTime)} · Logs unavailable
              </p>
            ),
          )}
        </div>
      )}

      {historyQuery.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button
            variant="secondary"
            onClick={() => historyQuery.fetchNextPage()}
            isLoading={historyQuery.isFetchingNextPage}
            loadingText="Loading"
          >
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};
